import React, { useEffect, useState } from 'react';
import { useMousePositionAsTrigger } from 'react-laag';
import Button from '../../../components/button';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import PopoverModalComponent from '../../../components/modal/popoverModal';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { PageHeader } from '../../../constants/style/common.styled';
import { type ModalStatusType } from '../../../types/common';
import { getFieldLevelPermissions, getNonFieldLevelPermissions } from '../../../utils';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import permissionCheck from '../../../utils/permission/permissionCheck';
import ManageFeatureSetModal from '../permission-management/manageFeatureSetModal';
import RolesPopoverContent from '../system-roles/rolesPopoverContent';
import UserPopoverContent from '../system-roles/userPopoverContent';
import { ROLE } from '../../../constants/common';
import { AccessProfileTableHeader } from './accessProfileTableHeader';

import { AccessProfileFacade } from './accessProfileService';
import { useGetAccessProfiles, useDeleteAccessProfile } from './useAccessProfileHooks';
import {
  type AccessProfilePageProps,
  type AccessProfileScope,
  ACCESS_PROFILE_CONFIG,
} from './types';
import AccessProfileModal from './accessProfileModal';
import AccessProfileFilterModal from './accessProfileFilterModal';
import AccessProfileDeleteConfirmModal from './accessProfileDeleteConfirmModal';
import { CustomerUserService } from '@wealthlane/api-services/wealthlane-customer-service';

interface AllUserInfoType {
  fullName: string;
  title: string;
  department: string;
}

const getInitialFormData = (scope: AccessProfileScope) => {
  if (scope === 'FeatureSet') {
    return {
      data: InitialValuesForm.featureSetModal,
      id: '',
    };
  }
  return {
    data: InitialValuesForm.addSystemRoleForm,
    id: '',
  };
};

const AccessProfilePage = ({ scope, hasId }: AccessProfilePageProps): JSX.Element => {
  const config = ACCESS_PROFILE_CONFIG[scope];

  const handleRequestCustomer = useWealthlaneCustomerApi().handleRequest;
  const handleRequest = handleRequestCustomer;

  const hasDeletePermission = permissionCheck({
    type: 'Delete',
    permission: config.permissionName,
    feature: config.permissionFeature,
  });

  const { tableInfo, setTableInfo, registerPermissionHandler } = useDynamicTable();

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const {
    value: isFilterModalOpen,
    setFalse: closeFilterModal,
    setTrue: openFilterModal,
    customValue: filterCustomValue,
    setCustomValue: setFilterCustomValue,
  } = useAdvanceBoolean<string>(false);

  const { data: listData, status } = useGetAccessProfiles(
    scope,
    handleRequest,
    tableInfo,
    filterCustomValue
  );

  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(getInitialFormData(scope));
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isManageModalOpen,
    setFalse: closeManageModal,
    setTrue: openManageModal,
  } = useBoolean(false);
  const {
    value: isDeleteConfirmModalOpen,
    setFalse: closeDeleteConfirmModal,
    setTrue: openDeleteConfirmModal,
  } = useBoolean(false);

  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteIsAssigned, setDeleteIsAssigned] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [checkedValues, setCheckedValues] = useState<any>([]);

  const [allPermission, setAllPermission] = useState({});
  const [allUsers, setAllUsers] = useState<AllUserInfoType[]>([]);
  const [popoverInfo, setPopoverInfo] = useState<string>('Role');

  const [headerList, setHeaderList] = useState<string[]>([]);

  const { mutate: deleteAccessProfile, isLoading: isDeleting } = useDeleteAccessProfile(
    scope,
    handleRequest,
    closeDeleteConfirmModal
  );

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: config.tableSchemaIdentifier,
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });

  const tablePrefName = scope === 'Role' ? 'System Roles' : 'Feature Set';
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, tablePrefName);

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: tablePrefName,
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const {
    hasMousePosition,
    resetMousePosition,
    handleMouseEvent,
    trigger,
  } = useMousePositionAsTrigger();

  useEffect(() => {
    if (scope === 'Role' && registerPermissionHandler) {
      registerPermissionHandler(() => (id: string, event: any, popoverInfoVal: string) => {
        if (popoverInfoVal === 'Role') {
          setPopoverInfo(popoverInfoVal);
          getProfileData(id);
        } else {
          setPopoverInfo(popoverInfoVal);
          getUserData(id);
        }
        if (event) {
          handleMouseEvent(event);
        }
      });
    }
  }, [registerPermissionHandler, scope]);

  const data = React.useMemo(() => listData?.items || [], [listData, status]);

  const getProfileData = (id: string, isDefaultAdmin?: boolean): void => {
    setLoading(true);
    AccessProfileFacade.getById(scope, id, handleRequest)
      .then((profileData: any) => {
        if (profileData != null) {
          const permissions = profileData.permissions ?? [];
          const filteredData = {
            ...profileData,
            ...(scope === 'Role' ? { roleName: profileData.name } : { name: profileData.name }),
            isDefaultAdmin,
            permissions: permissions.length > 0
              ? getNonFieldLevelPermissions(permissions).reduce(
                (a: any, v: any) => ({ ...a, [v.replaceAll('.', '-')]: true }),
                {}
              )
              : {},
            fieldLevelPermissions: permissions.length > 0
              ? getFieldLevelPermissions(permissions).reduce(
                (a: any, v: any) => ({ ...a, [v.replaceAll('.', '-')]: true }),
                {}
              )
              : {},
          };

          setInitialData({ id, data: filteredData });

          if (scope === 'Role') {
            const filterData = permissions.reduce(
              (a: any, v: any) => ({ ...a, [v]: true }),
              {}
            );
            setAllPermission(filterData);
          }

          setLoading(false);
        }
      })
      .catch((error: any) => {
        console.log('error', error);
        setLoading(false);
      });
  };

  const getUserData = (id: string): void => {
    setLoading(true);
    handleRequestCustomer(
      CustomerUserService.getApiCustomerApiAppCustomerUserCustomerUserListByRoleId(id)
    )
      .then((userData) => {
        if (userData != null) {
          const userInfo = userData.map((item) => ({
            fullName: item.fullName ?? '',
            title: item.customerTitle ?? '',
            department: item.customerDepartment ?? '',
          }));
          setAllUsers(userInfo);
          setLoading(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeletePreview = (id: string): void => {
    setDeleteLoading(true);
    AccessProfileFacade.deletePreview(scope, id, handleRequest)
      .then((response) => {
        if (response != null && response.success) {
          setDeleteTargetId(id);
          setDeleteMessage(
            response.message ?? `Are you sure you want to delete this ${config.entityLabel}?`
          );
          setDeleteIsAssigned(response.isAssigned ?? false);
          openDeleteConfirmModal();
        }
      })
      .catch(() => { })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleDeleteConfirm = (): void => {
    deleteAccessProfile(deleteTargetId);
  };

  const handleChecked = (values: string[]): void => {
    setCheckedValues(values);
  };

  useEffect(() => {
    if (checkedValues.length > 0) {
      setSelectedRowData(data.filter((row: any) => checkedValues.includes(row.id)));
    } else {
      setSelectedRowData(null);
    }
  }, [checkedValues]);

  const tableActions = [
    {
      action: 'View',
      id: `view${scope}Action`,
      onClick: (id: string, rowData: any) => {
        getProfileData(id, rowData?.isDefaultAdminRole);
        openModal();
        setModalStatus('View');
      },
    },
    {
      action: 'Edit',
      as: 'Edit',
      id: `edit${scope}Action`,
      onClick: (id: string, rowData: any) => {
        getProfileData(id, rowData?.isDefaultAdminRole);
        openModal();
        setModalStatus('Edit');
      },
    },
    ...(scope === 'Role'
      ? [
        {
          action: 'Copy',
          as: 'Create',
          id: `copy${scope}Action`,
          onClick: (id: string, rowData: any) => {
            setModalStatus('Add');
            setLoading(true);
            AccessProfileFacade.getById(scope, id, handleRequest)
              .then((profileData: any) => {
                if (profileData != null) {
                  const permissions = profileData.permissions ?? [];
                  const filteredData = {
                    ...profileData,
                    roleName: '',
                    isDefaultAdmin: rowData?.isDefaultAdminRole,
                    permissions: permissions.length > 0
                      ? getNonFieldLevelPermissions(permissions).reduce(
                        (a: any, v: any) => ({ ...a, [v.replaceAll('.', '-')]: true }),
                        {}
                      )
                      : {},
                    fieldLevelPermissions: permissions.length > 0
                      ? getFieldLevelPermissions(permissions).reduce(
                        (a: any, v: any) => ({ ...a, [v.replaceAll('.', '-')]: true }),
                        {}
                      )
                      : {},
                  };
                  setInitialData({ id: '', data: filteredData });
                  setLoading(false);
                }
              })
              .catch(() => setLoading(false));
            openModal();
          },
        },
      ]
      : []),
    {
      action: <div className='delete-label'>Delete</div>,
      as: 'Delete',
      id: `delete${scope}Action`,
      onClick: (id: string) => {
        handleDeletePreview(id);
      },
      isHidden: (rowData: any) =>
        !hasDeletePermission || !rowData?.isEditable || rowData?.isDefault,
    },
  ];

  const renderRightToolbarButton = () => {
    return (
      <div className='flex gap-4'>
        <Button
          as='Create'
          variant={'primary'}
          title={config.addButtonLabel}
          icon={'add_circle_outline'}
          id={`add${scope}Button`}
          onClick={() => {
            setInitialData(getInitialFormData(scope));
            setModalStatus('Add');
            openModal();
          }}
        />
        <Button
          as='Create'
          variant={'primary-outline'}
          title={'Bulk Edit'}
          icon={'discover_tune'}
          hasId={hasId}
          id={`bulkEdit${scope}Button`}
          onClick={() => {
            openManageModal();
          }}
          disabled={checkedValues?.length === 0}
        />
      </div>
    );
  };

  const tableHeaderList = AccessProfileTableHeader({
    scope,
    actions: schemaData?.columns,
    tableActions,
    hasMousePosition,
    resetMousePosition,
  })
    .filter((item: any) => item.isSubHeader)
    .map((item: any) => ({
      label: item.Header,
      value: item.accessor,
    }));

  const filteredColumns = (): any[] => {
    return AccessProfileTableHeader({
      scope,
      actions: schemaData?.columns,
      tableActions,
      hasMousePosition,
      resetMousePosition,
    }).filter((item: any) => !item.isSubHeader || headerList.includes(item.accessor));
  };

  const schema = React.useMemo(
    () => ({
      columns: filteredColumns(),
      options: schemaData?.options as any,
    }),
    [schemaData, hasMousePosition, headerList]
  );

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <div id='pageContainer' data-page={`accessProfile${scope}`}>
      <PageHeader id='pageHeader'>
        <Heading id='pageTitle' variant='h3' title={config.title} />
        <Heading id='pageSubtitle' variant='body-s' title={config.subtitle} />
      </PageHeader>

      <AnimationWrapper>
        {isModalOpen && (
          <AccessProfileModal
            id={`accessProfile${scope}Modal`}
            scope={scope}
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            initialData={initialData}
            handleRequest={handleRequest}
            modalStatus={modalStatus}
            setModalStatus={setModalStatus}
            loading={loading}
          />
        )}
      </AnimationWrapper>

      <AnimationWrapper>
        {isManageModalOpen && (
          <ManageFeatureSetModal
            id={`manage${scope}Modal`}
            isModalOpen={isManageModalOpen}
            closeModal={closeManageModal}
            checkedValues={checkedValues}
            setCheckedValues={setCheckedValues}
            handleRequest={handleRequest}
            permissionType={scope === 'FeatureSet' ? 'feature-set' : 'roles'}
            selectedRowData={selectedRowData}
          />
        )}
      </AnimationWrapper>

      <AnimationWrapper>
        {isFilterModalOpen && (
          <AccessProfileFilterModal
            id={`filter${scope}Modal`}
            scope={scope}
            isModalOpen={isFilterModalOpen}
            closeModal={closeFilterModal}
            handleRequest={handleRequest}
            setFilterCustomValue={setFilterCustomValue}
          />
        )}
      </AnimationWrapper>

      <AnimationWrapper>
        {isDeleteConfirmModalOpen && (
          <AccessProfileDeleteConfirmModal
            scope={scope}
            isShow={isDeleteConfirmModalOpen}
            handleClose={closeDeleteConfirmModal}
            handleSubmit={handleDeleteConfirm}
            message={deleteMessage}
            isAssigned={deleteIsAssigned}
            loading={isDeleting}
          />
        )}
      </AnimationWrapper>

      {scope === 'Role' && (
        <PopoverModalComponent
          children={
            popoverInfo === ROLE ? (
              <RolesPopoverContent permissionData={allPermission} loading={loading} />
            ) : (
              <UserPopoverContent data={allUsers} loading={loading} />
            )
          }
          hasMousePosition={hasMousePosition}
          trigger={trigger}
          popoverClassName='roles-popovers'
          resetMousePosition={resetMousePosition}
        />
      )}

      <DynamicTable
        id={`${scope}Table`}
        schema={schema}
        data={data}
        totalCounts={listData?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
        onFilterClick={(isResetFilter: boolean) => {
          if (isResetFilter) {
            setFilterCustomValue([]);
            setTableInfo({ ...tableInfo });
          } else {
            openFilterModal();
          }
        }}
        onChecked={handleChecked}
        checkedValues={checkedValues}
        showCheckBox={true}
      />
    </div>
  );
};

export default withDynamicTableContext(AccessProfilePage, false, axiosInstance);
